import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

export default function PrislistePage() {
  const bookingUrl = 'https://djursmassage.app4.geckobooking.dk/site/booking.php?show=new_booking&icCode=69a506a6f06a0ca7022b880e8f6bcb65b11332&bId=11332&aDIfH=1&dTpl=1&sSToken=267bea05a53684a4a7d7ad2e6bae564a';
  const giftcardUrl = 'https://djursmassage.app4.geckobooking.dk/site/index.php?icCode=e6868204e7e11708974faa3b47f24d3db11332&dTpl=1';
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    fetch('/_api/public/v2/content/priser?sort=sortering&order=asc')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.items && data.items.length > 0) {
          setPrices(data.items);
        }
      })
      .catch(() => {});
  }, []);

  const treatmentPrices = prices
    ? prices.filter(
        (p) =>
          p.terms?.priskategori?.includes('behandlingspriser') ||
          p.terms?.priskategori?.includes('tillaeg') ||
          !p.terms?.priskategori?.includes('klippekort')
      )
    : null;

  const klippekortPrices = prices
    ? prices.filter((p) => p.terms?.priskategori?.includes('klippekort'))
    : null;

  return (
    <>
      <SEOHead
        title="Prisliste & Klippekort | Djurs Massage"
        description="Oversigt over priser på massage, zoneterapi, voksbehandling, pedicure og klippekort hos Djurs Massage i Ryomgård. Priser fra 300 kr. (zoneterapi fra 600 kr.)."
        ogImage="https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/djurs-massage-klinik-ryomgaard.jpg"
      />

      {/* Clean Header - NO container box, NO paragraph above heading */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        <div className="max-w-3xl">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1E3A5F] font-serif leading-tight">
            Vores priser for velvære
          </h1>
          <p className="text-slate-700 text-sm sm:text-base mt-3 leading-relaxed font-normal">
            Hos Djurs Massage tilbyder vi professionelle behandlinger med gennemskuelige priser fra 300 kr. (zoneterapi fra 600 kr.). Ved køb af klippekort opnår du desuden en attraktiv rabat på dine behandlinger. Du kan betale med kort, MobilePay eller kontant.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-2.5 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-md transition-shadow"
            >
              <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
              <span>Book tid</span>
            </a>
            {/* Secondary CTA: strictly NO background color and NO hover background color */}
            <a
              href={giftcardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-2.5 bg-transparent hover:bg-transparent text-[#1E3A5F] hover:text-black border border-[#1E3A5F] text-xs sm:text-sm font-semibold rounded-full transition-colors"
            >
              <i className="fa-solid fa-gift text-[#C9A770]" aria-hidden="true" />
              <span>Køb klippekort / gavekort online</span>
            </a>
          </div>

          {/* Praktisk information boks */}
          <div className="mt-6 p-4 sm:p-5 bg-white border-l-4 border-[#C9A770] border-y border-r border-[#E8E1D5] shadow-2xs">
            <div className="flex items-start gap-3">
              <i className="fa-solid fa-circle-info text-[#C9A770] text-lg mt-0.5 shrink-0" aria-hidden="true" />
              <div className="space-y-1 text-xs sm:text-sm text-slate-800">
                <strong className="block text-[#1E3A5F] font-serif text-sm sm:text-base">Praktisk information forud for dit besøg</strong>
                <p>
                  Klinikken er en del af mit private hjem på Poppelvej 52 i Ryomgård, hvilket sikrer rolige, trygge og hyggelige omgivelser.
                </p>
                <p className="font-semibold text-[#1E3A5F]">
                  Medbring venligst selv eget lagen og håndklæde til behandlingen – ved zoneterapi bedes du meget gerne medbringe et stort håndklæde.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content directly on page canvas - NO card boxes wrapping headings and text */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-12">
        {/* Behandlingspriser Table */}
        <section className="pt-4">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1E3A5F] font-serif">
              Behandlingspriser
            </h2>
            <p className="text-slate-700 text-sm mt-1 leading-relaxed">
              Alle vores behandlinger udføres med fokus på din velvære og sundhed. Vælg mellem fysiurgisk massage, zoneterapi eller en afslappende relax massage – alle til en fast og fair timepris.
            </p>
          </div>

          {/* RAB Kvalitetsstempel & Forsikringsinformation */}
          <div className="mb-6 p-4 sm:p-5 bg-linear-to-r from-[#1E3A5F]/5 to-transparent border-l-4 border-[#1E3A5F] rounded-r-lg">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1.5 max-w-3xl">
                <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-[#1E3A5F] text-[#C9A770] text-xs font-bold tracking-wide">
                  <i className="fa-solid fa-award text-xs" aria-hidden="true" />
                  <span>RAB-godkendt – Massørfagets eneste kvalitetsstempel</span>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-[#1E3A5F] font-serif">
                  Din garanti for faglighed og forsikringsdækning
                </h3>
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  Hos Djurs Massage er Tine Vium Thorsen <strong>RAB-godkendt fysiurgisk massør</strong>. RAB (Registreret Alternativ Behandler) er det <em>eneste officielle kvalitetsstempel</em> for massører i Danmark og din sikkerhed for dokumenteret uddannelse.
                </p>
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  <strong>Udløser dækning fra sundhedsforsikringer:</strong> Langt de fleste private sundhedsforsikringer stiller krav om RAB-godkendelse for at dække massage. Klinikken er registreret ved <strong>Falck Healthcare</strong>, dækkes via <strong>AP Pension</strong> og andre private sundhedsordninger. (Zoneterapi ved Sophie er ligeledes RAB-godkendt med tilskud fra ”danmark”).
                </p>
              </div>
              <div className="shrink-0 flex flex-col gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E8E1D5] rounded shadow-2xs text-xs font-semibold text-[#1E3A5F]">
                  <i className="fa-solid fa-shield-check text-[#C9A770]" aria-hidden="true" />
                  <span>RAB-godkendt massør</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E8E1D5] rounded shadow-2xs text-xs font-semibold text-[#1E3A5F]">
                  <i className="fa-solid fa-hospital-user text-[#C9A770]" aria-hidden="true" />
                  <span>Sundhedsforsikring & Falck</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E8E1D5] rounded shadow-2xs text-xs font-semibold text-[#1E3A5F]">
                  <i className="fa-solid fa-file-invoice-dollar text-[#C9A770]" aria-hidden="true" />
                  <span>Dækkes af AP Pension</span>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto border-t border-slate-300">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-[#1E3A5F] text-xs uppercase tracking-wider text-[#1E3A5F]">
                  <th className="py-3 px-3 sm:px-4 font-bold">Behandling</th>
                  <th className="py-3 px-3 sm:px-4 font-bold">Varighed / Detaljer</th>
                  <th className="py-3 px-3 sm:px-4 font-bold text-right">Pris</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-300 text-xs sm:text-sm text-slate-700">
                {treatmentPrices && treatmentPrices.length > 0 ? (
                  treatmentPrices.map((item, idx) => (
                    <tr
                      key={item.id || idx}
                      className={`hover:bg-slate-50 transition-colors ${
                        item.fremhaevet ? 'bg-amber-50/40' : ''
                      }`}
                    >
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span>{item.ydelse}</span>
                        </div>
                        {item.undertekst && (
                          <span className="block text-xs font-normal text-slate-500">
                            {item.undertekst}
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">{item.varighed}</td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        {item.pris}
                      </td>
                    </tr>
                  ))
                ) : (
                  <>
                    <tr className="hover:bg-slate-50 transition-colors bg-amber-50/40">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span>Fysiurgisk massage & Sportsmassage</span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#1E3A5F] text-[#C9A770]">
                            <i className="fa-solid fa-award text-[9px]" aria-hidden="true" />
                            RAB-godkendt
                          </span>
                        </div>
                        <span className="block text-xs font-normal text-slate-600">
                          Tine er RAB-godkendt massør (kvalitetsstempel) · Dækkes af sundhedsforsikringer, Falck Healthcare & AP Pension · 30 min (300 kr.) / 45 min (450 kr.) / 60 min (600 kr.) / 90 min (900 kr.)
                        </span>
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        30 / 45 / 60 / 90 minutter
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        Fra 300 kr.
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        Tillæg for ansigtsmassage
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        15 minutter
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        150 kr.
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        Zoneterapi (voksne & babyer)
                        <span className="block text-xs font-normal text-slate-500">RAB-godkendt · Tilskud fra Sygeforsikringen ”danmark” og sundhedsforsikringer</span>
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        Konsultation og behandling (60-75 min)
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        Fra 600 kr.
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-50 transition-colors bg-amber-50/40">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        EMS dragt (KUN dragt, ingen træning)
                        <span className="block text-xs font-normal text-slate-600">30 min. afsat (inkl. af-/påklædning & rengøring). Vi tilbyder ikke gratis prøvetræning.</span>
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        30 minutter (KUN dragt – kan bruges alene eller før massage)
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        300 kr.
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        Afslappende (relax) massage
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        30 min (ryg: 300 kr.) · 60 min (hele kroppen: 600 kr.)
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        Fra 300 kr.
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        Voksbehandling & Bikini
                        <span className="block text-xs font-normal text-slate-500">Udføres med den rette voks af kosmetolog Dianna (ben, arme, ryg, bikini mv.)</span>
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        Afhænger af område (se Gecko booking)
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        Fra 300 kr.
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        Threading (trådning) af bryn & overlæbe
                        <span className="block text-xs font-normal text-slate-500">Præcis, skånsom hårfjerning i ansigtet uden voks</span>
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        15-25 minutter
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        Se Gecko booking
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        Pedicure & Fodbehandling
                        <span className="block text-xs font-normal text-slate-500">Hård hud files/slibes skånsomt (beskæres ikke) · Pleje af negle & neglebånd</span>
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        Kosmetologisk fodpleje
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        Fra 300 kr.
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                        Tillæg for hovedbund- og ansigtsmassage
                      </td>
                      <td className="py-3.5 px-3 sm:px-4">
                        15 minutter
                      </td>
                      <td className="py-3.5 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                        150 kr.
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Klippekort og tilbud */}
        <section className="pt-6 border-t border-[#E8E1D5]">
          <div className="mb-6">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1E3A5F] font-serif">
              Klippekort og tilbud
            </h2>
            <p className="text-slate-700 text-sm mt-1 leading-relaxed">
              Ønsker du at gøre din velvære til en regelmæssig del af din hverdag? Vi tilbyder fordelagtige klippekort, der giver dig mulighed for at spare på dine behandlinger hos Djurs Massage. Perfekt til både dig selv og som gave.
            </p>
          </div>

          {/* Dedicated Klippekort Cards Grid with box-shadow on hover and black text on hover */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Card 1: 2 timer */}
            <div className="group bg-white p-6 border border-[#E8E1D5] hover:shadow-lg transition-shadow flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-[#1E3A5F] group-hover:text-black transition-colors font-serif">
                  Klippekort til behandling
                </h3>
                <div className="text-2xl font-extrabold text-[#1E3A5F] group-hover:text-black transition-colors">
                  2 timer
                </div>
                <p className="text-xs text-slate-600 group-hover:text-black transition-colors leading-relaxed">
                  Ideelt til to grundige behandlinger eller en serie af fokuserede ryg- og nakkemassager.
                </p>
              </div>
              <a
                href={giftcardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#1E3A5F] text-white text-xs font-bold rounded-full hover:shadow-sm transition-shadow"
              >
                <i className="fa-solid fa-gift text-[#C9A770]" aria-hidden="true" />
                <span>Køb 2 timers klippekort</span>
              </a>
            </div>

            {/* Card 2: 3 timer */}
            <div className="group bg-white p-6 border border-[#E8E1D5] hover:shadow-lg transition-shadow flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-[#1E3A5F] group-hover:text-black transition-colors font-serif">
                  Klippekort til behandling
                </h3>
                <div className="text-2xl font-extrabold text-[#1E3A5F] group-hover:text-black transition-colors">
                  3 timer
                </div>
                <p className="text-xs text-slate-600 group-hover:text-black transition-colors leading-relaxed">
                  Vores mest valgte kort. Giver kontinuitet i behandlingen og mærkbart langvarigt velvære.
                </p>
              </div>
              <a
                href={giftcardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#C9A770] text-[#12243A] text-xs font-bold rounded-full hover:shadow-sm transition-shadow"
              >
                <i className="fa-solid fa-gift" aria-hidden="true" />
                <span>Køb 3 timers klippekort</span>
              </a>
            </div>

            {/* Card 3: 4 timer */}
            <div className="group bg-white p-6 border border-[#E8E1D5] hover:shadow-lg transition-shadow flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-[#1E3A5F] group-hover:text-black transition-colors font-serif">
                  Klippekort til behandling
                </h3>
                <div className="text-2xl font-extrabold text-[#1E3A5F] group-hover:text-black transition-colors">
                  4 timer
                </div>
                <p className="text-xs text-slate-600 group-hover:text-black transition-colors leading-relaxed">
                  Den maksimale fleksibilitet og besparelse til forebyggende eller dybdegående behandlinger over tid.
                </p>
              </div>
              <a
                href={giftcardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#1E3A5F] text-white text-xs font-bold rounded-full hover:shadow-sm transition-shadow"
              >
                <i className="fa-solid fa-gift text-[#C9A770]" aria-hidden="true" />
                <span>Køb 4 timers klippekort</span>
              </a>
            </div>
          </div>

          {/* Table summary as scraped */}
          <div className="overflow-x-auto border-t border-slate-300">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-[#1E3A5F] text-xs uppercase tracking-wider text-[#1E3A5F]">
                  <th className="py-3 px-3 sm:px-4 font-bold">Klippekort</th>
                  <th className="py-3 px-3 sm:px-4 font-bold text-right">Timer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-300 text-xs sm:text-sm text-slate-700">
                <tr className="hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                    Klippekort til behandling
                  </td>
                  <td className="py-3 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                    2 timer
                  </td>
                </tr>
                <tr className="hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                    Klippekort til behandling
                  </td>
                  <td className="py-3 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                    3 timer
                  </td>
                </tr>
                <tr className="hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-3 sm:px-4 font-semibold text-[#1E3A5F]">
                    Klippekort til behandling
                  </td>
                  <td className="py-3 px-3 sm:px-4 font-bold text-[#1E3A5F] text-right whitespace-nowrap">
                    4 timer
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Få mere at vide om vores tilbud */}
        <section className="pt-6 border-t border-[#E8E1D5]">
          <div className="max-w-3xl space-y-3">
            <h2 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] font-serif">
              Få mere at vide om vores tilbud
            </h2>
            <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
              Er du interesseret i at vide mere om vores klippekort eller andre særlige tilbud? Kontakt os, eller besøg vores side for klippekort/gavekort for yderligere information og for at finde den løsning, der passer bedst til dig.
            </p>
            <div className="pt-2 flex flex-wrap items-center gap-3">
              <a
                href={giftcardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-md transition-shadow"
              >
                <i className="fa-solid fa-gift text-[#C9A770]" aria-hidden="true" />
                <span>Se vores klippekort og gavekort</span>
              </a>
              {/* Secondary CTA: strictly NO background color and NO hover background color */}
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 bg-transparent hover:bg-transparent text-[#1E3A5F] hover:text-black border border-[#1E3A5F] text-xs sm:text-sm font-semibold rounded-full transition-colors"
              >
                <span>Kontakt klinikken</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
